<template>
  <div class="pp-stack" data-test="pp-tab-treatment">
    <div v-if="txsignSentence" class="pp-callout" data-test="pp-txsign">
      <i class="fa fa-pencil-square-o pp-callout__ic" aria-hidden="true"></i>
      <span>
        <span class="pp-callout__t">Treatment plan awaiting signature</span>
        <span class="pp-callout__s">{{ txsignSentence }}</span>
      </span>
    </div>

    <div
      :class="txPresenterOn || hostDrawsSide() ? 'pp-cols pp-cols--tx' : 'pp-stack'"
      data-test="pp-treatment-cols"
    >
      <PanelCard
        title="Unscheduled treatment"
        icon="fa-stethoscope"
        :count="rows.length ? `${rows.length} procedure${rows.length === 1 ? '' : 's'}` : '—'"
        :table="rows.length > 0"
        test-id="pp-treatment-unscheduled"
      >
        <table v-if="rows.length" class="pp-tbl">
          <thead>
            <tr><th>Pri</th><th>Code</th><th>Procedure</th><th>Tooth</th><th>Provider</th><th>Tx plan date</th></tr>
          </thead>
          <tbody>
            <tr v-for="(t, i) in rows" :key="i">
              <td :class="t.Priority ? 'pp-tbl__pri' : 'pp-tbl__pri--none'">{{ t.Priority || '—' }}</td>
              <td class="pp-tbl__code">{{ t.ProcCode }}</td>
              <td>{{ t.Descript }}</td>
              <td>{{ tooth(t) }}</td>
              <td>{{ t.Provider || '—' }}</td>
              <td class="pp-tbl__num">{{ date(t.DateTP) }}</td>
            </tr>
          </tbody>
        </table>
        <p v-else class="pp-empty">No unscheduled treatment.</p>
      </PanelCard>

      <PanelCard
        v-if="txPresenterOn"
        title="Treatment plans signed"
        icon="fa-check-square-o"
        :count="signedPlans.length || '—'"
        :table="signedPlans.length > 0"
        test-id="pp-signed-plans"
      >
        <p v-if="signedPlansLoading && !signedPlans.length" class="pp-empty">Loading…</p>
        <table v-else-if="signedPlans.length" class="pp-tbl pp-tbl--narrow">
          <thead><tr><th>Treatment plan</th><th>Signed on</th><th>Consent forms</th></tr></thead>
          <tbody>
            <tr v-for="p in signedPlans" :key="p.treatPlanNum">
              <td>{{ p.heading || date(p.dateTP) }}</td>
              <td class="pp-tbl__num">{{ signedOn(p.signedAt) }}</td>
              <td>
                <span v-if="!(p.consentForms || []).length" class="pp-muted">None</span>
                <span
                  v-for="(f, i) in p.consentForms || []"
                  :key="i"
                  class="pp-pill"
                  :class="f.completed ? 'pp-pill--green' : 'pp-pill--amber'"
                >{{ f.name }}{{ f.completed ? '' : ' · not signed' }}</span>
              </td>
            </tr>
          </tbody>
        </table>
        <p v-else class="pp-empty">No signed treatment plans yet.</p>
      </PanelCard>
      <!-- Off: whatever the host puts here (the page's Treatment Presenter upgrade card). -->
      <slot v-else-if="hostDrawsSide()" name="side"></slot>
    </div>
  </div>
</template>

<script>
/**
 * Treatment — unscheduled treatment (Patient Profile's own query) and Tx Presenter's signed plans.
 *
 * The design's fee / estimated-insurance / patient columns are NOT here: Patient Profile's query
 * does not select fees, and the Phase 7 decision (2026-09-25) is to show only what Teamio already
 * loads. The unscheduled total is on the `unsched` alert when a hygiene visit carries it.
 *
 * Two columns (Unscheduled left, signed plans right; stacked below the view's 1100px line). With
 * Tx Presenter off the right-hand column is the host's `side` slot (the page's upgrade card); no
 * slot, no second column.
 *
 * ⛔ No prop named `patNum`, `siteID` or `loading` — mixin.js computeds silently replace them.
 */
import moment from 'moment-timezone';
import PanelCard from '../PanelCard.vue';
import { formatOdDate, toothLabel } from '@/utils/patientPanel/format';
import { describeAlert } from '@/utils/scheduleView/alerts';

export default {
  name: 'TreatmentTab',

  components: { PanelCard },

  props: {
    payload: { type: Object, default: () => ({}) },
    /** This patient's alert details, keyed by alert — only the visible ones. */
    alertDetails: { type: Object, default: () => ({}) },
    signedPlans: { type: Array, default: () => [] },
    signedPlansLoading: { type: Boolean, default: false },
    txPresenterOn: { type: Boolean, default: false },
    dateFormat: { type: String, default: 'MM/DD/YYYY' },
    timezone: { type: String, default: '' },
  },

  computed: {
    rows() {
      return (this.payload && this.payload.unscheduledTx) || [];
    },
    txsignSentence() {
      if (!this.alertDetails || !this.alertDetails.txsign) return '';
      return describeAlert('txsign', this.alertDetails.txsign);
    },
  },

  methods: {
    /** True when the host fills #side. A method, not a computed: $scopedSlots is not reactive. */
    hostDrawsSide() {
      return !!this.$scopedSlots.side;
    },
    tooth: toothLabel,
    date(v) {
      return formatOdDate(v, this.dateFormat) || '—';
    },
    /**
     * `signedAt` is a real instant (not an Open Dental wall-clock date), so it is shown in the
     * PRACTICE's timezone — the same rule as Patient Profile's column.
     */
    signedOn(value) {
      if (!value) return '—';
      const m = this.timezone && moment.tz.zone(this.timezone) ? moment(value).tz(this.timezone) : moment(value);
      return m.isValid() ? m.format(this.dateFormat) : '—';
    },
  },
};
</script>
